import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import {
    ANALYTICS_NOT_AVAILABLE,
    DX_TYPE,
    ELITE_STATUS,
    JOURNEY_TYPE,
    LOYALTY_ACQUISITION_SOURCE_NONE,
    LOYALTY_CONSTRAINT_TYPE_COUNT,
    LOYALTY_DEFAULT_TIER_TARGET,
    LOYALTY_DISPLAY_STATUS,
    LOYALTY_STATUS,
    TRIP_TYPE,
    UTM_SOURCE_ORGANIC
} from '@/constants/components/loyaltyAnalytics'
import type { V2SearchResponse } from '@/types/V2SearchTypes'
import { getCookie } from '@/utils/general/cookie'
import { isServer } from '@/utils'
import type { ProcessedData } from '@/utils/srp/types'

dayjs.extend(customParseFormat)

const getUtmSource = (): string => {
    if (!isServer()) {
        const parts = window.location?.search?.split('utm_source=')
        if (parts && parts.length > 1) {
            return parts.pop()?.split('&')?.[0] || getCookie('utm_source') || UTM_SOURCE_ORGANIC
        }
    }
    return getCookie('utm_source') || UTM_SOURCE_ORGANIC
}

const deriveEliteStatus = ({
    loyaltyStatus,
    displayStatus
}: {
    loyaltyStatus?: string | null
    displayStatus?: string | null
}): string | null => {
    if (!loyaltyStatus) return null

    const status = loyaltyStatus.toUpperCase()
    const display = displayStatus?.toUpperCase() ?? ''

    // displayStatus is more specific, so check it before the base status
    if (display === LOYALTY_DISPLAY_STATUS.EXPIRED) return ELITE_STATUS.EXPIRED
    if (display === LOYALTY_DISPLAY_STATUS.EXPIRING_SOON || display === LOYALTY_DISPLAY_STATUS.TRACKER_EXPIRING_SOON)
        return ELITE_STATUS.EXPIRING_SOON
    if (display === LOYALTY_DISPLAY_STATUS.ACTIVATING_SOON) return ELITE_STATUS.ACTIVATING_SOON

    if (status === LOYALTY_STATUS.SUSPENDED) return ELITE_STATUS.SUSPENDED
    if (status === LOYALTY_STATUS.ACTIVE) return ELITE_STATUS.ACTIVE
    if (status === LOYALTY_STATUS.INACTIVE) return ELITE_STATUS.INACTIVE

    return status.toLowerCase()
}

/** Elite member attributes read off the search programContext. */
export const getSrpLoyaltyAttributes = (data: V2SearchResponse | null | undefined) => {
    const loyaltyTracker = data?.programContext?.loyaltyTrackerDetails?.[0]
    const tierTracker = loyaltyTracker?.tierTrackerDetails?.[0]
    const constraint = tierTracker?.constraintList?.find(c => c.type === LOYALTY_CONSTRAINT_TYPE_COUNT)
    const subscriptionDetails = loyaltyTracker?.subscriptionDetails
    const totalBookings = constraint ? constraint.current + constraint.upcoming : 0
    const target = constraint?.target ?? LOYALTY_DEFAULT_TIER_TARGET

    return {
        elite_status: deriveEliteStatus({
            loyaltyStatus: loyaltyTracker?.loyaltyStatus,
            displayStatus: loyaltyTracker?.displayStatus
        }),
        elite_progress: data ? `${totalBookings}/${target}` : null,
        partner_program_source: subscriptionDetails?.acquisitionSource?.toLowerCase() ?? LOYALTY_ACQUISITION_SOURCE_NONE,
        elite_unlock_date: tierTracker?.allotmentDate ?? null,
        elite_membership_end_date: subscriptionDetails?.endDate ?? tierTracker?.viewExpiryDate ?? null,
        utm_source: getUtmSource()
    }
}

const formatSearchDate = (date: string | undefined | null): string => {
    if (!date) return ANALYTICS_NOT_AVAILABLE
    const parsed = dayjs(date, 'DD/MM/YYYY', true)
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ANALYTICS_NOT_AVAILABLE
}

/** Search context attributes from the processed query. */
export const getSrpSearchAttributes = (
    processedData: Partial<ProcessedData> | null | undefined,
    searchData?: { searchId?: string } | null
) => {
    const isIntl = Boolean(processedData?.isIntl)
    const returnDate = processedData?.returnDate

    return {
        a_search_id: searchData?.searchId || ANALYTICS_NOT_AVAILABLE,
        a_origin: processedData?.source || ANALYTICS_NOT_AVAILABLE,
        a_destination: processedData?.destination || ANALYTICS_NOT_AVAILABLE,
        a_journey_type: returnDate ? JOURNEY_TYPE.ROUND_TRIP : JOURNEY_TYPE.ONEWAY,
        a_trip_type: isIntl ? TRIP_TYPE.INTERNATIONAL : TRIP_TYPE.DOMESTIC,
        a_departure_date: formatSearchDate(processedData?.departDate),
        a_return_date: formatSearchDate(returnDate),
        a_adult_count: String(processedData?.travellers?.adults ?? ANALYTICS_NOT_AVAILABLE),
        a_child_count: String(processedData?.travellers?.children ?? ANALYTICS_NOT_AVAILABLE),
        a_dx: isIntl ? DX_TYPE.INTERNATIONAL : DX_TYPE.DOMESTIC,
        a_airline: ANALYTICS_NOT_AVAILABLE,
        a_flight_number: ANALYTICS_NOT_AVAILABLE
    }
}
